import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import Input from "../inputs/Input";
import SelectInput from "../inputs/SelectInput";
import TextAreaInput from "../inputs/TextAreaInput";

const PLAN_OPTIONS = [
  { id: "ESSENTIAL", label: "Essential" },
  { id: "PRO", label: "PRO" },
];
const CYCLE_OPTIONS = [
  { id: "MONTHLY", label: "Mensal" },
  { id: "QUARTERLY", label: "Trimestral" },
  { id: "SEMIANNUAL", label: "Semestral" },
  { id: "ANNUAL", label: "Anual" },
];

const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

/** O banco guarda JSON, mas cupons antigos podem ter vindo como texto. */
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  return String(value)
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
};

const buildDefaultValues = (coupon) => ({
  code: coupon?.code || "",
  name: coupon?.name || "",
  partnerName: coupon?.partnerName || "",
  description: coupon?.description || "",
  discountType: coupon?.discountType || "PERCENTAGE",
  discountValue: coupon?.discountValue ?? "",
  maxDiscountValue: coupon?.maxDiscountValue ?? "",
  minPurchaseValue: coupon?.minPurchaseValue ?? 0,
  usageLimit: coupon?.usageLimit ?? "",
  allowedPlanIds: toArray(coupon?.allowedPlanIds),
  allowedBillingCycles: toArray(coupon?.allowedBillingCycles),
  firstPurchaseOnly: coupon?.firstPurchaseOnly ?? true,
  startsAt: toDateInput(coupon?.startsAt),
  expiresAt: toDateInput(coupon?.expiresAt),
  isActive: coupon?.isActive ?? true,
});

/**
 * Grupo de checkboxes que o react-hook-form agrega em um array de ids —
 * vários inputs com o mesmo `name` viram `["PRO", "ESSENTIAL"]`.
 */
const CheckboxGroup = ({ label, hint, name, options, register }) => (
  <fieldset className="w-full rounded-lg border border-gray-300 px-3 pb-3 pt-2">
    <legend className="px-1 text-sm text-black">{label}</legend>
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {options.map((option) => (
        <label
          key={option.id}
          htmlFor={`${name}-${option.id}`}
          className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
        >
          <input
            id={`${name}-${option.id}`}
            type="checkbox"
            value={option.id}
            className="h-4 w-4 rounded border-slate-300"
            {...register(name)}
          />
          {option.label}
        </label>
      ))}
    </div>
    {hint && <p className="mt-2 text-xs text-slate-500">{hint}</p>}
  </fieldset>
);

const CouponForm = ({ coupon, onSubmit, onCancel, isSaving, errorMessage }) => {
  const defaultValues = useMemo(() => buildDefaultValues(coupon), [coupon]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const discountType = watch("discountType");
  const isPercentage = discountType === "PERCENTAGE";

  const submit = (data) => {
    onSubmit({
      ...data,
      // Campos opcionais vão como null para o backend limpar o valor anterior
      // em vez de guardar string vazia.
      maxDiscountValue:
        data.maxDiscountValue === "" ? null : data.maxDiscountValue,
      usageLimit: data.usageLimit === "" ? null : data.usageLimit,
      startsAt: data.startsAt || null,
      expiresAt: data.expiresAt || null,
      // Nada marcado significa "vale para todos" — o backend guarda null.
      allowedPlanIds: data.allowedPlanIds?.length ? data.allowedPlanIds : null,
      allowedBillingCycles: data.allowedBillingCycles?.length
        ? data.allowedBillingCycles
        : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-5 pb-6">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Input
          label="Código"
          name="code"
          required
          register={register}
          error={errors?.code && "Informe o código do cupom"}
          style={{ textTransform: "uppercase" }}
        />
        <Input label="Nome interno" name="name" register={register} />
      </div>

      <Input
        label="Parceiro / campanha"
        name="partnerName"
        register={register}
      />

      <TextAreaInput label="Descrição" name="description" register={register} />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <SelectInput
          label="Tipo de desconto"
          name="discountType"
          register={register}
        >
          <option value="PERCENTAGE">Percentual (%)</option>
          <option value="FIXED">Valor fixo (R$)</option>
        </SelectInput>

        <Input
          label={isPercentage ? "Desconto (%)" : "Desconto (R$)"}
          name="discountValue"
          type="number"
          step="0.01"
          min="0"
          max={isPercentage ? "100" : undefined}
          required
          register={register}
          error={errors?.discountValue && "Informe o valor do desconto"}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Input
          label="Teto do desconto (R$)"
          name="maxDiscountValue"
          type="number"
          step="0.01"
          min="0"
          register={register}
          disabled={!isPercentage}
        />
        <Input
          label="Valor mínimo da compra (R$)"
          name="minPurchaseValue"
          type="number"
          step="0.01"
          min="0"
          register={register}
        />
      </div>

      <Input
        label="Limite de usos (vazio = ilimitado)"
        name="usageLimit"
        type="number"
        min="0"
        register={register}
      />

      <CheckboxGroup
        label="Planos permitidos"
        hint="Nenhum marcado: o cupom vale para todos os planos."
        name="allowedPlanIds"
        options={PLAN_OPTIONS}
        register={register}
      />

      <CheckboxGroup
        label="Ciclos permitidos"
        hint="Nenhum marcado: o cupom vale para todos os períodos."
        name="allowedBillingCycles"
        options={CYCLE_OPTIONS}
        register={register}
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Input
          label="Início da validade"
          name="startsAt"
          type="date"
          register={register}
        />
        <Input
          label="Expira em"
          name="expiresAt"
          type="date"
          register={register}
        />
      </div>

      <label className="flex items-center gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300"
          {...register("firstPurchaseOnly")}
        />
        Válido apenas na primeira compra
      </label>

      <label className="flex items-center gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300"
          {...register("isActive")}
        />
        Cupom ativo
      </label>

      {errorMessage && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {errorMessage}
        </p>
      )}

      <div className="flex items-center justify-center gap-3">
        <Button
          type="submit"
          isProcessing={isSaving}
          processingSpinner={
            <AiOutlineLoading className="h-5 w-5 animate-spin" />
          }
          disabled={isSaving}
        >
          {coupon?.id ? "Salvar alterações" : "Criar cupom"}
        </Button>
      </div>
    </form>
  );
};

export default CouponForm;
